'use client'

import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/styles'

const DEFAULT_SCRIPT_SRC = 'https://js.knock-ai.com/components/load.js'

type KnockAiProps = {
	dataId: string
	scriptSrc?: string
	skeleton?: ReactNode
}
const DefaultKnockSkeleton = (
	<Skeleton className={cn('max-w-[250px] w-full h-[45px] rounded-full')} />
)
export function KnockAi({
	dataId,
	scriptSrc = DEFAULT_SCRIPT_SRC,
	skeleton = DefaultKnockSkeleton,
}: KnockAiProps) {
	const containerRef = useRef<HTMLDivElement | null>(null)
	const injectedRef = useRef(false)
	const id = useId()
	const [loaded, setLoaded] = useState(false)

	useEffect(() => {
		const container = containerRef.current
		if (!container || container.querySelector(`#${id}`)) return

		// Prevent double-injection in React StrictMode (dev) and re-renders
		if (injectedRef.current) return
		injectedRef.current = true

		// Create a script element INSIDE the container so Knock's loader
		// uses document.currentScript.parentNode as the mount target.
		const script = document.createElement('script')
		script.src = scriptSrc
		script.async = true
		script.setAttribute('data-id', dataId)
		script.setAttribute('id', id)
		script.setAttribute('data-target', `#${id}`)
		script.onload = () => {
			setLoaded(true)
		}

		container.appendChild(script)

		return () => {
			injectedRef.current = false
		}
	}, [dataId, scriptSrc, id])

	return (
		<>
			{loaded
				? null
				: // Show a skeleton while the Knock AI component is loading
					skeleton}
			<div className="kn-injected-content">
				<div ref={containerRef} />
			</div>
		</>
	)
}
